import {useCallback, useMemo} from 'react';

import {fuzzySearchBuilder} from '../../helpers/fuzzySearchHelpers';
import {ApiCircle, ApiCircleRelationships, ApiCircles, CircleModel} from '../../models/api/circleModel';
import {fetchApiAsync} from '../../services/http/http';
import {makeResourceListNoPagination} from '../helpers/resourceListPagination/resourceListNoPagination';
import {usePersistedListMutation, useResourceList} from '../helpers/resourceTableHooks';
import {
  SettingsInvalidateQueriesOptions,
  useSettingsInvalidateQueries,
  useSettingsQuery,
  UseSettingsQueryOptions,
} from '../helpers/useSettingsQuery';
import {CircleListMutation, circleListMutations, CircleListPageType} from './circlesMutations';

/*
 * Consts.
 */

const circleQueryKeyFactory = {
  id: (circleId: number) => ['circle', {id: circleId}],
  all: ['circles'],
  relationships: (circleId: number) => ['circleRelationships', {id: circleId}],
};

/*
 * Fetchers.
 */

export function useFetchCircle(circleId: number, options?: UseSettingsQueryOptions) {
  const {data, asyncStatus} = useSettingsQuery(
    circleQueryKeyFactory.id(circleId),
    async ({company, cancelToken}) =>
      fetchApiAsync(ApiCircle, `${company.url}/circles/${circleId}`, {cancelToken}),
    options,
  );

  return {circle: data, status: asyncStatus};
}

export function useInvalidateCircle() {
  const invalidate = useSettingsInvalidateQueries();

  return useCallback(
    (circleId: number, options?: SettingsInvalidateQueriesOptions) => {
      invalidate(circleQueryKeyFactory.id(circleId), options);
    },
    [invalidate],
  );
}

export function useFetchCircles(query?: string, options?: UseSettingsQueryOptions) {
  const pagination = useMemo(
    () =>
      makeResourceListNoPagination<CircleModel>({
        makeUrl: (company) => `${company.url}/namespaces/global/circles`,
        apiType: ApiCircles,
      }),
    [],
  );

  const {items, moreStatus, loadMore} = useResourceList(circleQueryKeyFactory.all, pagination, options);
  const {mutate} = usePersistedListMutation<
    CircleListMutation,
    CircleListPageType,
    typeof circleListMutations
  >(circleQueryKeyFactory.all, circleListMutations);

  const circles = useMemo(() => {
    if (query === undefined || query.trim() === '' || !items) {
      return items;
    }

    return fuzzySearcher(items, query);
  }, [items, query]);

  return useMemo(() => ({circles, moreStatus, loadMore, mutate}), [circles, loadMore, moreStatus, mutate]);
}

export function useFetchCircleRelationships(circleId: number, options?: UseSettingsQueryOptions) {
  const {data, asyncStatus} = useSettingsQuery(
    circleQueryKeyFactory.relationships(circleId),
    async ({company, cancelToken}) =>
      fetchApiAsync(ApiCircleRelationships, `${company.url}/circles/${circleId}/relationships`, {
        cancelToken,
      }),
    options,
  );

  return {relationships: data, status: asyncStatus};
}

/*
 * Helpers.
 */

// TODO @circles: Use ES once Circles are indexed.
const fuzzySearcher = fuzzySearchBuilder<CircleModel>((circle) => [circle.name]);
